import React, { useEffect } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../custom/BackButton';
import ActionIcon from '../../../custom/ActionIcon';
import { createdUpdatedInfo, getTsDate } from '../../../../utils';
import { Printer, SelectOption } from '../../../../globals/interfaces';
import { useZodForm, ZodFormProvider } from '../../../custom/ZodForm/ZodFormProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { PrinterSchema, PrinterSchemaUpdate } from './validation';
import { createZodInput } from '../../../custom/ZodForm/TypedInput';
import { createZodSelect } from '../../../custom/ZodForm/ZodSelect';

const FormInput = createZodInput<Printer>();
const Select = createZodSelect<Printer>();

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    s_email: string;
    units: Array<SelectOption>;
    onSubmit: (data: Printer, update?: boolean) => void;
    deleteItem: (id: number) => Promise<void>;
    printer: Printer | undefined;
}

export default function ModalManagePrinters({
    modal,
    setModal,
    s_email,
    units,
    onSubmit,
    deleteItem,
    printer,
}: Props) {

    const update = Boolean(printer);
    const methods = useZodForm<Printer>({
        resolver: update ? zodResolver(PrinterSchemaUpdate) : zodResolver(PrinterSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        context: {
            valueAsNumber: true
        }
    });

    const {
        handleSubmit,
        reset,
        clearErrors,
        trigger,
        formState: { errors, isValid, isDirty },
        requiredFields
    } = methods;

    useEffect(() => {
        if (modal) {
            // Clear all previous errors
            clearErrors();
            const now = getTsDate();

            if (printer) {
                reset({
                    ...printer,
                    modifiedAt: now,
                    modifiedBy: s_email
                });
            } else {
                reset({
                    name: '',
                    unit: '',
                    ipAddress: '',
                    createdAt: now,
                    createdBy: s_email,
                    modifiedAt: now,
                    modifiedBy: s_email
                });
            }

            trigger();
        }
    }, [modal, printer, reset, clearErrors, s_email]);

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} Zebra Printer</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <ZodFormProvider methods={methods} requiredFields={requiredFields}>
                    <FormInput label="Name" name={'name'} />
                    <FormInput label="IP Address" name={'ipAddress'} />
                    <Select
                        options={units}
                        name={'unit'}
                        label={'Unit'}
                    />
                </ZodFormProvider>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>
                        {createdUpdatedInfo(printer, true)}
                    </Label>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={() => deleteItem(printer.id)}
                                    simulateLoading
                                />
                            )
                        }
                        <ActionIcon
                            type={'save'}
                            disabled={!isDirty || !isValid}
                            onClick={handleSubmit(data => onSubmit(data, update))}
                            simulateLoading
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;